import { _, helpers, React, ts, ui } from '_core';

import { prepareWeigtsVsTimeData } from './helpers';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  definition: ts.types.signal.OptimizedModel;
  file: string;
  fullScreen: boolean;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, file, definition, fullScreen, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          columns: ['build_date', 'weights'],
          columns_inclusion: 'STARTS_WITH',
        },
      });
      if (response) {
        setData(response.data);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const formattedData = React.useMemo(() => {
    if (!data) return [];
    const newData = helpers.buildName.signalTableVarName(data, definition);
    return prepareWeigtsVsTimeData(newData);
  }, [data]);

  const customDownloadFormatting = (lData: ts.types.widgets.TableData) => {
    const localData = helpers.csv.transpose(lData as any, 'date', 'var');
    return localData.map((row) => {
      const newRow = { var: row['var'] } as typeof row;
      // Add dates in order
      _.without(Object.keys(row), 'var')
        .reverse()
        .forEach((date) => {
          newRow[date] = row[date];
        });
      return newRow;
    });
  };

  return (
    <ui.LineChart
      id={id}
      data={formattedData}
      fullScreen={fullScreen}
      readOnly={readOnly}
      alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
      customDownloadFormatting={customDownloadFormatting}
      exportTitle={title}
      yAxisLabel="Signal Weights"
      xAxisLabel="Time"
      numberFormat="#.##%"
    />
  );
};

export default Chart;
